import { faUpload } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Paper, styled, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { ALL_INSTITUTIONS_CODE, CollapsibleTable, InstitutionBreadcrumbs, InstitutionsContext, PageLayout, RouteLoading } from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ImageDocument, ImageUpload, LookupTimePoints } from '../../api/dtos';
import { ImageUploadUpdaterProvider } from '../../components/providers/form/ImageUploadUpdaterProvider';
import MriImageUploadsProvider from '../../components/providers/form/MriImageUploadsProvider';
import { ImageUploadUpdaterContext } from '../../contexts/ImageUploadUpdaterContext';
import MriImageUploadsContext from '../../hooks/context/MriImageUploadsContext';
import useRunDicomSignalR from '../../hooks/signalR/useRunDicomSignalR';
import ImageCell from './ImageCell';
import ImageUploadDialog from './ImageUploadDialog';

const PaperContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    width: '100%',
}));

const HeaderBar = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
}));

const timepointLabel = (id: number | undefined): string => {
    switch (id) {
        case LookupTimePoints.Baseline: return 'Baseline';
        case LookupTimePoints.EighteenMonth: return '18 Month';
        default: return '';
    }
};

interface IInner {
    patientId: number;
    canAdministerOpms: boolean;
    isSiteUSer: boolean;
    isCentralReviewer: boolean;
}

const PatientUploadsInner: FC<IInner> = ({ patientId, canAdministerOpms, isSiteUSer, isCentralReviewer }) => {
    const { data: uploads, loading } = useContext(MriImageUploadsContext);
    const imageUploadUpdater = useContext(ImageUploadUpdaterContext);
    const { dicomId, dateTime } = useRunDicomSignalR();

    useEffect(() => {
        if (dicomId) {
            imageUploadUpdater.notify(dicomId);
        }
    }, [dicomId, dateTime, imageUploadUpdater]);

    const patientUpload: ImageUpload | undefined = useMemo(() => {
        return uploads?.find(u => u.patientId === patientId);
    }, [uploads, patientId]);

    const { data: institutions } = useContext(InstitutionsContext);

    const canUploadForPatient = useMemo(() => {
        if (patientUpload?.institutionName == null) {
            return false;
        }

        return institutions?.some(i => i.name === patientUpload.institutionName) === true;
    }, [institutions, patientUpload?.institutionName]);

    const docs: ImageDocument[] = useMemo(() => {
        return patientUpload?.mriDocuments ?? [];
    }, [patientUpload]);

    const columns: GridColDef<ImageDocument>[] = useMemo(() => [
        { 
            field: 'name', 
            headerName: 'Name', 
            flex: 1, 
            minWidth: 200 ,
            sortable: true,
            valueGetter: (_value, row) => row.name ?? '',
            sortComparator: (v1, v2) => String(v1 ?? '').localeCompare(String(v2 ?? ''), undefined, { sensitivity: 'base' }),
        },
        {
            field: 'lookupTimePoints',
            headerName: 'Timepoint',
            width: 160,
            renderCell: rowData => timepointLabel(rowData.row.lookupTimePoints),
            sortable: true,
        },
        {
            field: 'status',
            headerName: 'Status',
            filterable: false,
            sortable: false,
            width: 300,
            renderCell: rowData => (
                <ImageCell
                    canAdministerOpms={canAdministerOpms}
                    isSiteUSer={isSiteUSer}
                    isCentralReviewer={isCentralReviewer}
                    initialImageDocument={rowData.row}
                    uploadId={patientUpload?.id}
                    displayImage={true}
                    documentType="MRI"
                />
            )
        }
    ], [canAdministerOpms, isSiteUSer, isCentralReviewer, patientUpload?.id]);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogUploadId, setDialogUploadId] = useState<number | undefined>(undefined);

    const onUploadClick = () => {
        if (patientUpload?.id == null) return;
        setDialogUploadId(patientUpload.id);
        setDialogOpen(true);
    };

    if (loading) {
        return <RouteLoading />;
    }

    return (
        <>
            <HeaderBar>
                <Typography variant="h5">
                    {patientUpload?.participantNumber ?? `Patient ${patientId}`}
                </Typography>
                {
                    !isCentralReviewer && (isSiteUSer || canAdministerOpms) && canUploadForPatient && patientUpload?.id != null && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<FontAwesomeIcon icon={faUpload} />}
                            onClick={onUploadClick}
                        >
                            Upload MRI
                        </Button>
                    )
                }
            </HeaderBar>

            <CollapsibleTable
                rows={docs}
                loading={false}
                columns={columns as GridColDef<ImageDocument>[]}
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                title={`MRI Uploads (${docs.length})`}
                pluralizeTitle={false}
                entityName={"MRI Upload"}
                disabledTableText={"No MRI uploads for this participant yet."}
                startCollapsed={false}
            />

            {
                dialogUploadId != null && (
                    <ImageUploadDialog
                        dialogOpen={dialogOpen}
                        setDialogOpen={setDialogOpen}
                        imageUploadId={dialogUploadId}
                        setImageUploadId={setDialogUploadId}
                        createNew
                    />
                )
            }
        </>
    );
};

const MriImageUploadPage: FC = () => {
    const { institutionCode, patientId } = useParams();

    const parsedInstitutionCode = useMemo(() => {
        return institutionCode !== ALL_INSTITUTIONS_CODE ? institutionCode : undefined;
    }, [institutionCode]);

    const parsedPatientId = useMemo(() => {
        const n = Number(patientId);
        return Number.isFinite(n) ? n : undefined;
    }, [patientId]);

    const { data: institutions } = useContext(InstitutionsContext);

    const institution = useMemo(() => {
        return institutions?.find(i => i.code === parsedInstitutionCode);
    }, [parsedInstitutionCode, institutions]);

    const {
        permissions: [canAdministerOpms, isSiteUSer, canCentralReview],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [Permission.OpmsAdminister, Permission.IsSiteUser, Permission.CanCentralReview],
        undefined,
        undefined,
        institution?.id
    );

    const isCentralReviewer = useMemo(() => {
        return !canAdministerOpms && canCentralReview;
    }, [canAdministerOpms, canCentralReview]);

    if (permissionsLoading || parsedPatientId == null) {
        return <RouteLoading />;
    }

    return (
        <PageLayout
            heading="MRI Uploads "
            breadcrumbs={<InstitutionBreadcrumbs />}
        >
            <ImageUploadUpdaterProvider>
                <PaperContainer>
                    <MriImageUploadsProvider>
                        <PatientUploadsInner
                            patientId={parsedPatientId}
                            canAdministerOpms={canAdministerOpms}
                            isSiteUSer={isSiteUSer}
                            isCentralReviewer={isCentralReviewer}
                        />
                    </MriImageUploadsProvider>
                </PaperContainer>
            </ImageUploadUpdaterProvider>
        </PageLayout>
    );
};

export default MriImageUploadPage;
